import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import Input from "./atoms/Input";
import Select from "./atoms/Select";
import { CLOTHES_ITEM_LIST } from "../utils/productNameGuide";

const GENDER_LIST = [
  { text: "공용/생략", value: "" },
  { text: "남성", value: "남성" },
  { text: "여성", value: "여성" },
  { text: "키즈", value: "키즈" },
];

// 의류(CLOTHES) 상품명 구성 입력 — 칸별로 입력하면 규칙 순서대로 조합해 상품명을 자동 완성
// 매니저어드민 ProductNameComposer 와 동일 로직(모바일 레이아웃만 다름) — 규칙 변경 시 함께 수정할 것
// 브랜드는 브랜드 선택값(brandKr)이 자동으로 맨 앞에 포함됨(별도 입력칸 없음)
// 성별은 부모 소유(gender/onGenderChange) — 상담 등록요청 단계에선 상품명 조합에만 사용
function ProductNameComposer({ category, name, brandKr, gender, onGenderChange, onCompose }) {
  const [pieces, setPieces] = useState({ model: "", feature: "", item: "" });
  // 구성 입력을 실제로 사용했는지 — 사용 전에는 상품명을 덮어쓰지 않음(수정 시 기존값 보호)
  const [touched, setTouched] = useState(false);
  // 아이템 칸 화면 표시값 — pieces.item(확정값)은 표준 목록 값만 담김
  const [itemQuery, setItemQuery] = useState("");
  const [itemOpen, setItemOpen] = useState(false);

  const build = (nextPieces, nextGender) =>
    [brandKr, nextPieces.model, nextGender, nextPieces.feature, nextPieces.item]
      .map((piece) => (piece || "").trim())
      .filter(Boolean)
      .join(" ");

  // 브랜드 변경 시 이미 구성 입력을 쓰고 있으면 상품명 재조합
  useEffect(() => {
    if (touched) onCompose(build(pieces, gender));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandKr]);

  // 상품명이 비워지면(상품 추가 완료 후 폼 리셋 등) 구성 입력도 초기화
  useEffect(() => {
    if (!name) {
      setPieces({ model: "", feature: "", item: "" });
      setItemQuery("");
      setTouched(false);
    }
  }, [name]);

  if (category !== "CLOTHES") return null;

  const handlePiece = (key) => (e) => {
    let value = e.target.value;
    if (key === "model") value = value.toUpperCase(); // 모델번호는 대문자 규칙
    const next = { ...pieces, [key]: value };
    setPieces(next);
    setTouched(true);
    onCompose(build(next, gender));
  };

  const commitItem = (value) => {
    const next = { ...pieces, item: value };
    setPieces(next);
    setTouched(true);
    onCompose(build(next, gender));
  };

  const filteredItems = itemQuery.trim()
    ? CLOTHES_ITEM_LIST.filter((it) => it.includes(itemQuery.trim()))
    : CLOTHES_ITEM_LIST;

  const handleItemChange = (e) => {
    const value = e.target.value;
    setItemQuery(value);
    setItemOpen(true);
    // 표준 목록 값과 정확히 일치할 때만 상품명에 반영 — 아이템 종류 통일값 강제
    if (CLOTHES_ITEM_LIST.includes(value.trim())) commitItem(value.trim());
    else if (pieces.item) commitItem("");
  };

  const handleItemSelect = (value) => (e) => {
    e.preventDefault(); // blur로 목록이 닫히기 전에 선택 처리
    commitItem(value);
    setItemQuery(value);
    setItemOpen(false);
  };

  const handleItemBlur = () => {
    setItemOpen(false);
    setItemQuery(pieces.item); // 목록에 없는 입력은 확정값으로 되돌림
  };

  const handleGender = (e) => {
    const value = e.target.value;
    onGenderChange(value);
    // 성별 선택만으로는 상품명을 덮어쓰지 않음 — 구성 입력을 쓰는 중일 때만 재조합
    if (touched) onCompose(build(pieces, value));
  };

  return (
    <ComposerBox>
      <ComposerTitle>상품명 구성 (의류)</ComposerTitle>
      <ComposerHint>
        브랜드는 자동 포함 · 입력하면 상품명이 자동 완성됩니다
      </ComposerHint>
      <Row2>
        <Input
          value={pieces.model}
          placeholder="모델번호"
          onChange={handlePiece("model")}
        />
        <Select value={gender || ""} onChange={handleGender}>
          {GENDER_LIST.map((g) => (
            <option key={g.value} value={g.value}>
              {g.text}
            </option>
          ))}
        </Select>
      </Row2>
      <Input
        value={pieces.feature}
        placeholder="특징 (라인·소재·패턴 순)"
        onChange={handlePiece("feature")}
      />
      <ItemSelectWrap>
        <Input
          value={itemQuery}
          placeholder="아이템 종류 검색"
          onChange={handleItemChange}
          onFocus={() => setItemOpen(true)}
          onBlur={handleItemBlur}
        />
        {itemOpen && filteredItems.length > 0 && (
          <ItemDropdown>
            {filteredItems.map((it) => (
              <button
                key={it}
                type="button"
                className={it === pieces.item ? "active" : ""}
                onMouseDown={handleItemSelect(it)}
              >
                {it}
              </button>
            ))}
          </ItemDropdown>
        )}
      </ItemSelectWrap>
    </ComposerBox>
  );
}

const ComposerBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f7f9fc;
  border: 1px solid #e1e7f0;
  border-radius: 10px;
`;
const ComposerTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #003580;
`;
const ComposerHint = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: -4px;
`;
const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;
const ItemSelectWrap = styled.div`
  position: relative;
`;
const ItemDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 4px 0;
  z-index: 2;

  & > button {
    display: block;
    width: 100%;
    padding: 11px 14px;
    border: 0;
    background: none;
    text-align: left;
    font-size: 14px;
    color: #555555;
    cursor: pointer;

    &:hover {
      background: #f2f6fb;
    }
    &.active {
      color: #003580;
      font-weight: 600;
    }
  }
`;

export default ProductNameComposer;
